import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "../styles/legal.module.css";

const Privacy = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <div className={styles.legalContainer}>
      <div className={styles.legalContent}>
        <header className={styles.legalHeader}>
          <Link to="/" className={styles.backLink}>←Home</Link>
          <h1>Privacy Policy</h1>
          <p className={styles.lastUpdated}>Last updated: {new Date().toLocaleDateString()}</p>
        </header>

        <div className={styles.legalBody}>
          <section>
            <h2>1. Introduction</h2>
            <p>
              Memory Curve ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use our web application and services.
            </p>
            <p>
              Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the application.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Personal Information</h3>
            <p>We may collect personal information that you voluntarily provide to us when you:</p>
            <ul>
              <li>Register for an account</li>
              <li>Use our services</li>
              <li>Contact us for support</li>
              <li>Subscribe to our notifications</li>
            </ul>

            <h3>2.2 Types of Data Collected</h3>
            <ul>
              <li><strong>Account Information:</strong> Email address, username, and securely hashed passwords</li>
              <li><strong>Revision Data:</strong> Your learning progress, study sessions, and performance statistics</li>
              <li><strong>Payment Information:</strong> Billing details processed securely through Stripe (we do not store credit card information)</li>
              <li><strong>Calendar Data:</strong> When you choose to integrate with Google Calendar, we access your calendar events as permitted</li>
              <li><strong>Communication Data:</strong> Email address for notifications and account-related communications</li>
            </ul>

            <h3>2.3 Automatically Collected Information</h3>
            <p>We may automatically collect certain information about your device and usage patterns, including:</p>
            <ul>
              <li>IP address and browser type</li>
              <li>Usage statistics and application interactions</li>
              <li>Log files and error reports</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li><strong>Provide Our Services:</strong> Display your revision data, track learning progress, and optimize your study schedule</li>
              <li><strong>Account Management:</strong> Create and maintain your account, authenticate users, and provide customer support</li>
              <li><strong>Communications:</strong> Send you email notifications about your revision schedule (when enabled)</li>
              <li><strong>Payment Processing:</strong> Process payments for premium features through our secure payment processor, Stripe</li>
              <li><strong>Calendar Integration:</strong> Sync your study schedule with Google Calendar when you choose to enable this feature</li>
              <li><strong>Improve Our Services:</strong> Analyze usage patterns to enhance user experience and application functionality</li>
              <li><strong>Security:</strong> Monitor for suspicious activity and protect against fraud</li>
            </ul>
          </section>

          <section>
            <h2>4. Data Security</h2>
            <p>
              We implement appropriate security measures to protect your personal information:
            </p>
            <ul>
              <li><strong>Password Security:</strong> All passwords are securely hashed using industry-standard encryption methods</li>
              <li><strong>Data Encryption:</strong> Sensitive data is encrypted in transit and at rest</li>
              <li><strong>Access Controls:</strong> Limited access to personal data on a need-to-know basis</li>
              <li><strong>Regular Security Updates:</strong> We regularly update our security protocols and systems</li>
            </ul>
            <p>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially
              acceptable means to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </section>

          <section>
            <h2>5. Data Sharing and Disclosure</h2>
            <p>
              <strong>We do not sell, trade, or share your personal data with third parties.</strong> We may only disclose your information in the following circumstances:
            </p>
            <ul>
              <li><strong>Service Providers:</strong> We may share data with trusted third-party service providers (like Stripe for payments and Google for calendar integration) who assist us in operating our application</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required to do so by law or in response to valid legal requests</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction</li>
              <li><strong>Safety and Security:</strong> We may disclose information to protect the rights, property, or safety of Memory Curve, our users, or others</li>
            </ul>
          </section>

          <section>
            <h2>6. Third-Party Services</h2>
            
            <h3>6.1 Stripe Payment Processing</h3>
            <p>
              We use Stripe to process payments. Stripe has its own privacy policy and security measures.
              We do not store your credit card information on our servers.
            </p>

            <h3>6.2 Google Calendar Integration</h3>
            <p>
              When you choose to connect your Google Calendar, we access your calendar data to help you integrate your study schedule.
              This integration is optional and can be disconnected at any time.
            </p>
          </section>

          <section>
            <h2>7. Email Communications</h2>
            <p>
              You may receive email notifications related to:
            </p>
            <ul>
              <li>Revision reminders and study schedule updates</li>
              <li>Account security and important service announcements</li>
              <li>Updates to our terms or privacy policy</li>
            </ul>
            <p>
              You can opt out of non-essential email notifications through your account settings. However, we may still send you
              important account-related and security communications.
            </p>
          </section>

          <section>
            <h2>8. Data Retention</h2>
            <p>
              We retain your personal information only as long as necessary to provide our services and fulfill the purposes outlined in this policy.
              When you delete your account, we will delete your personal data, though some information may be retained for legal or administrative purposes.
            </p>
          </section>

          <section>
            <h2>9. Your Rights and Choices</h2>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from non-essential email communications</li>
            </ul>
            <p>
              To exercise these rights, please contact us at the email address provided below.
            </p>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>
              Our service is not directed to children under the age of 13. We do not knowingly collect personal information from children under 13.
              If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy
              on this page and sending you an email notification. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p>Email: <a href="mailto:support@memorycurve.fr">support@memorycurve.fr</a></p>
            <p>
              We will respond to your inquiry within a reasonable timeframe.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy; 